import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTheme } from '@mui/material';

import {
  getStandardWatermarkAttributes,
  getWatermarkConfig
} from '@/components/Chart/hooks/options/useChartFunctions';
import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import type { TimeSeriesPointCustom } from '@/components/Chart/utils/buildChartLineSeries';
import { createTooltip } from '@/components/Chart/utils/createTooltip';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { fullScreenChartPalette } from '@/constants/constants';
import { Permission } from '@/entities/permissions';
import { Highcharts } from '@/lib/highcharts';
import usePermission from '@/shared/hooks/usePermission';

import type HighchartsType from 'highcharts/highstock';

const RANGE_SELECTOR_BUTTONS: HighchartsType.RangeSelectorButtonsOptions[] = [
  { type: 'month', count: 1, text: '1m' },
  { type: 'month', count: 3, text: '3m' },
  { type: 'month', count: 6, text: '6m' },
  { type: 'year', count: 1, text: '1y' },
  { type: 'ytd', text: 'YTD' },
  { type: 'all', text: 'All' }
];

const STOCK_TOOLS_BUTTONS = [
  'indicators',
  'simpleShapes',
  'lines',
  'crookedLines',
  'measure',
  'advanced',
  'toggleAnnotations',
  'verticalLabels',
  'flags',
  'zoomChange',
  'currentPriceIndicator'
] as const;

const ROLLOVER_TOOLTIP_BACKGROUND = '#333';
const AXIS_LABEL_FONT_SIZE = '10px';
const INDICATOR_PANE_SEPARATOR_COLOR = '#4f5458';
const INDICATOR_PANE_SEPARATOR_WIDTH = 6;
const Y_AXIS_LABEL_OFFSET = 0;
const Y_AXIS_MAX_PADDING = 0.1;

const ROLLOVER_TOOLTIP_STYLES = {
  bodyRow:
    'font-size: 10px; font-style: normal;font-weight: 500;justify-content: space-between;height: 19px; display: flex;',
  container: 'display: flex; flex-direction: column; width:350px;',
  dateColumn: 'width: 65px; padding-left: 8px; border-left: 1px solid gray;',
  headerRow: 'font-size: 8px; display: flex; font-weight: 400;justify-content: space-between;',
  nameColumn:
    'width: 152px; padding-right: 8px; border-right: 1px solid gray; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;',
  periodColumn: 'width: 65px; padding-left: 8px; padding-right: 8px;',
  valueBox:
    'font-size:10px; line-height:13px; height:14px; width:55px; padding-left:4px; padding-right:4px; text-align: start;',
  valueContainer: 'height 19px; padding-left:8px; padding-top:3px'
} as const;

const ROLLOVER_TOOLTIP_COLUMN_WIDTHS = {
  date: 65,
  name: 152,
  period: 65
} as const;

export type FullScreenLegendSeries = {
  alias?: string;
  code?: string;
  color: string;
  currency?: string;
  id: string;
  name: string;
  period?: string;
  periodType?: string;
  timeRef?: string;
  units?: string;
  visible: boolean;
};

type CreateFullScreenStockOptionsParams = {
  chartSeries: ExtendedSeriesOptionsType[];
  hiddenSeriesIds: string[];
  tooltip?: HighchartsType.TooltipOptions;
  useSmallWatermark?: boolean;
};

type StockSeriesOptions = NonNullable<HighchartsType.Options['series']>[number];

type FullScreenRolloverFormatter = (
  allChartSeries: HighchartsType.Series[],
  hoveredPointXValue: string | number | undefined
) => string;

type UseFullScreenChartParams = {
  chartId: string;
  chartSeries: ExtendedSeriesOptionsType[];
};

const DARK_MODE_WATERMARK = getWatermarkConfig('/watermark-black.svg');
const LIGHT_MODE_WATERMARK = getWatermarkConfig('/watermark-color.svg');

type FullscreenWatermarkKey = 'darkMode' | 'lightMode';

type FullscreenWatermarks = Partial<Record<FullscreenWatermarkKey, HighchartsType.SVGElement>>;

type FullscreenWatermarkChart = HighchartsType.Chart & {
  fullscreenWatermarks?: FullscreenWatermarks;
};

type FullscreenNavigationBindings = {
  fullscreenYAxisSyncInstalled?: boolean;
  resizeYAxes?: (removedYAxisProps?: unknown) => void;
};

type FullscreenRangeSelector = {
  fullscreenRangeSelectorSyncInstalled?: boolean;
  positionButtons?: () => void;
  render?: (min?: number, max?: number) => void;
  zoomText?: {
    hide: () => void;
  };
};

type FullscreenRuntimeChart = FullscreenWatermarkChart & {
  navigationBindings?: FullscreenNavigationBindings;
  rangeSelector?: FullscreenRangeSelector;
};

const formatFullscreenYAxisLabel = function (this: { value: unknown }): string {
  const value = Number(this.value);
  return Number.isFinite(value)
    ? Number.parseFloat(value.toFixed(2)).toString()
    : String(this.value);
};

const AXIS_LABEL_X = 12;
const AXIS_LABEL_Y = 3;

const createFullScreenYAxisLabelsOptions = (): HighchartsType.YAxisOptions['labels'] => ({
  align: 'left',
  formatter: formatFullscreenYAxisLabel,
  x: AXIS_LABEL_X,
  y: AXIS_LABEL_Y,
  style: {
    fontSize: AXIS_LABEL_FONT_SIZE
  }
});

const createFullScreenYAxisResizeOptions = (
  resize: HighchartsType.YAxisOptions['resize'] = {}
): HighchartsType.YAxisOptions['resize'] => ({
  ...resize,
  enabled: resize.enabled ?? true,
  lineColor: INDICATOR_PANE_SEPARATOR_COLOR,
  lineWidth: INDICATOR_PANE_SEPARATOR_WIDTH
});

const createFullScreenYAxisOptions = (
  axisOptions: HighchartsType.YAxisOptions = {}
): HighchartsType.YAxisOptions => ({
  labels: createFullScreenYAxisLabelsOptions(),
  maxPadding: Y_AXIS_MAX_PADDING,
  offset: Y_AXIS_LABEL_OFFSET,
  opposite: true,
  resize: createFullScreenYAxisResizeOptions(axisOptions.resize),
  title: axisOptions.title ?? { text: undefined }
});

const isInternalYAxis = (axis: HighchartsType.Axis): boolean =>
  (axis.options as HighchartsType.YAxisOptions & { isInternal?: boolean }).isInternal === true;

const needsFullScreenYAxisOptions = (axisOptions: HighchartsType.YAxisOptions): boolean => {
  const labels = axisOptions.labels ?? {};
  const resize = axisOptions.resize ?? {};

  return (
    axisOptions.maxPadding !== Y_AXIS_MAX_PADDING ||
    axisOptions.offset !== Y_AXIS_LABEL_OFFSET ||
    axisOptions.opposite !== true ||
    labels.align !== 'left' ||
    labels.formatter !== formatFullscreenYAxisLabel ||
    labels.style?.fontSize !== AXIS_LABEL_FONT_SIZE ||
    labels.x !== AXIS_LABEL_X ||
    labels.y !== AXIS_LABEL_Y ||
    resize.lineColor !== INDICATOR_PANE_SEPARATOR_COLOR ||
    resize.lineWidth !== INDICATOR_PANE_SEPARATOR_WIDTH
  );
};

export const applyFullscreenYAxisLayout = (
  chart: HighchartsType.Chart,
  shouldRedraw = false
): void => {
  let hasUpdatedAxis = false;

  for (const axis of chart.yAxis) {
    const axisOptions = axis.options as HighchartsType.YAxisOptions;
    if (isInternalYAxis(axis) || !needsFullScreenYAxisOptions(axisOptions)) continue;

    axis.update(createFullScreenYAxisOptions(axisOptions), false);
    hasUpdatedAxis = true;
  }

  if (hasUpdatedAxis && shouldRedraw) {
    chart.redraw(false);
  }
};

const installFullscreenYAxisSync = (chart: HighchartsType.Chart): void => {
  const navigationBindings = (chart as FullscreenRuntimeChart).navigationBindings;
  if (!navigationBindings?.resizeYAxes || navigationBindings.fullscreenYAxisSyncInstalled) return;

  const resizeYAxes = navigationBindings.resizeYAxes.bind(navigationBindings);
  navigationBindings.resizeYAxes = removedYAxisProps => {
    resizeYAxes(removedYAxisProps);
    applyFullscreenYAxisLayout(chart);
  };
  navigationBindings.fullscreenYAxisSyncInstalled = true;
};

export const applyFullscreenRangeSelectorLayout = (chart: HighchartsType.Chart): void => {
  const rangeSelector = (chart as FullscreenRuntimeChart).rangeSelector;
  if (!rangeSelector) return;

  rangeSelector.zoomText?.hide();
  rangeSelector.positionButtons?.();
};

const installFullscreenRangeSelectorSync = (chart: HighchartsType.Chart): void => {
  const rangeSelector = (chart as FullscreenRuntimeChart).rangeSelector;
  if (!rangeSelector?.render || rangeSelector.fullscreenRangeSelectorSyncInstalled) return;

  const render = rangeSelector.render.bind(rangeSelector);
  rangeSelector.render = (min, max) => {
    render(min, max);
    applyFullscreenRangeSelectorLayout(chart);
  };
  rangeSelector.fullscreenRangeSelectorSyncInstalled = true;
};

const getSeriesId = (series: ExtendedSeriesOptionsType, index: number): string =>
  String(series.id ?? series.name ?? `series-${index + 1}`);

const getSeriesName = (series: ExtendedSeriesOptionsType, index: number): string => {
  if (typeof series.name === 'string' && series.name.length > 0) return series.name;
  if (typeof series.custom.Title === 'string' && series.custom.Title.length > 0) {
    return series.custom.Title;
  }
  return getSeriesId(series, index);
};

const getSeriesColor = (series: ExtendedSeriesOptionsType, index: number): string => {
  const paletteIndex =
    typeof series.custom.combinedIndex === 'number' ? series.custom.combinedIndex : index;

  return (
    series.custom.color ??
    fullScreenChartPalette[Math.abs(paletteIndex) % fullScreenChartPalette.length]
  );
};

const clonePoint = (point: unknown): unknown => {
  if (Array.isArray(point)) return [...point];
  if (!point || typeof point !== 'object') return point;
  const custom = 'custom' in point ? point.custom : undefined;

  return {
    ...point,
    custom: custom && typeof custom === 'object' ? { ...custom } : custom
  };
};

const getPointX = (point: unknown): number | undefined => {
  if (Array.isArray(point)) return typeof point[0] === 'number' ? point[0] : undefined;
  if (!point || typeof point !== 'object' || !('x' in point)) return undefined;
  return typeof point.x === 'number' ? point.x : undefined;
};

const getInitialRangeButtonIndex = (chartSeries: ExtendedSeriesOptionsType[]): number => {
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const hasMoreThanOneYear = chartSeries.some(series => {
    const firstPoint = series.data.find(point => getPointX(point) !== undefined);
    const firstX = getPointX(firstPoint);
    return firstX !== undefined && firstX < oneYearAgo.getTime();
  });

  return hasMoreThanOneYear ? 3 : 1;
};

const toHighchartsSeries = (
  series: ExtendedSeriesOptionsType,
  index: number,
  hiddenSeriesIds: string[]
): HighchartsType.SeriesOptionsType => {
  const id = getSeriesId(series, index);

  return {
    ...series,
    id,
    name: getSeriesName(series, index),
    color: getSeriesColor(series, index),
    custom: { ...series.custom },
    data: series.data.map(clonePoint),
    showInNavigator: true,
    type: series.type,
    visible: !hiddenSeriesIds.includes(id)
  } as HighchartsType.SeriesOptionsType;
};

const createTooltipColumn = (content: string, style: string): string =>
  `<div style="${style}">${content}</div>`;

const createTooltipValueBox = ({
  backgroundColor,
  textColor,
  value
}: {
  backgroundColor: HighchartsType.ColorType;
  textColor: string;
  value: string;
}): string =>
  `<div style="background-color:${backgroundColor}; color:${textColor}; ${ROLLOVER_TOOLTIP_STYLES.valueBox}">${value}</div>`;

const createRolloverTooltipHeader = (): string => {
  const columns = [
    {
      content: 'Short Title',
      style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.name}px;`
    },
    {
      content: 'PeriodAbs',
      style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.period}px;`
    },
    {
      content: 'Last date',
      style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.date}px;`
    },
    { content: '', style: 'width: 10%;' }
  ]
    .map(({ content, style }) => createTooltipColumn(content, style))
    .join('');

  return `<div style="${ROLLOVER_TOOLTIP_STYLES.container}"><div style="${ROLLOVER_TOOLTIP_STYLES.headerRow}">${columns}</div>`;
};

const createRolloverTooltipBodyRow = ({
  custom,
  formatDateString,
  point,
  valueTextColor,
  value
}: {
  custom: TimeSeriesPointCustom | undefined;
  formatDateString: (date: string, variant: DateTextVariants) => string;
  point: HighchartsType.Point;
  valueTextColor: string;
  value: string;
}): string => {
  const nameColumn = createTooltipColumn(point.series.name, ROLLOVER_TOOLTIP_STYLES.nameColumn);
  const periodColumn = createTooltipColumn(
    custom?.periodAbs ?? '',
    ROLLOVER_TOOLTIP_STYLES.periodColumn
  );
  const date = formatDateString(new Date(point.x).toJSON(), DateTextVariants.FULL);
  const dateColumn = createTooltipColumn(date, ROLLOVER_TOOLTIP_STYLES.dateColumn);
  const valueBox = createTooltipValueBox({
    backgroundColor: point.series.color ?? '#F8F8F8',
    textColor: valueTextColor,
    value
  });
  const valueContainer = createTooltipColumn(valueBox, ROLLOVER_TOOLTIP_STYLES.valueContainer);

  return `<div style="${ROLLOVER_TOOLTIP_STYLES.bodyRow}">${nameColumn}${periodColumn}${dateColumn}${valueContainer}</div>`;
};

const useFullScreenRolloverFormatter = (): FullScreenRolloverFormatter => {
  const theme = useTheme();
  const { formatDateString } = useDateText();
  const valueTextColor = theme.palette.background.default;

  return useCallback(
    (allChartSeries, hoveredPointXValue) =>
      createTooltip({
        allChartSeries,
        hoveredPointXValue,
        createHeader: createRolloverTooltipHeader,
        createBodyRow: (point, custom, value) =>
          createRolloverTooltipBodyRow({
            custom,
            formatDateString,
            point,
            valueTextColor,
            value
          })
      }),
    [formatDateString, valueTextColor]
  );
};

export const createFullScreenRolloverTooltip = (
  createTooltipFormatter: FullScreenRolloverFormatter,
  textColor = '#F8F8F8'
): HighchartsType.TooltipOptions => ({
  backgroundColor: ROLLOVER_TOOLTIP_BACKGROUND,
  borderRadius: 0,
  className: 'tooltip-fullscreen-chart',
  enabled: true,
  followPointer: true,
  formatter(): string {
    return createTooltipFormatter(this.series.chart.series, this.x);
  },
  positioner() {
    return { x: 100, y: 80 };
  },
  shape: 'rect',
  split: false,
  style: {
    color: textColor,
    fontFamily: 'Cairo, sans-serif',
    zIndex: 1303
  },
  useHTML: true
});

const createWatermarkImage = (
  chart: HighchartsType.Chart,
  key: FullscreenWatermarkKey
): HighchartsType.SVGElement => {
  const watermark = key === 'darkMode' ? DARK_MODE_WATERMARK : LIGHT_MODE_WATERMARK;
  return chart.renderer
    .image(watermark.dataUri)
    .addClass(`gx-fullscreen-watermark gx-fullscreen-watermark-${key}`)
    .add();
};

export const applyFullscreenWatermarkLayout = (
  chart: FullscreenWatermarkChart,
  useSmallWatermark = false
) => {
  const watermarks = (chart.fullscreenWatermarks ??= {});
  watermarks.darkMode ??= createWatermarkImage(chart, 'darkMode');
  watermarks.lightMode ??= createWatermarkImage(chart, 'lightMode');

  const attrs = getStandardWatermarkAttributes({
    chart,
    aspectRatio: DARK_MODE_WATERMARK.aspectRatio,
    useSmallWatermark
  });

  [watermarks.darkMode, watermarks.lightMode].forEach(image => {
    image.attr({
      height: attrs.height,
      width: attrs.width,
      zIndex: attrs.zIndex
    });
    image.align(
      {
        align: attrs.align ?? 'right',
        verticalAlign: attrs.verticalAlign ?? 'bottom',
        x: attrs.x,
        y: attrs.y
      },
      false,
      'plotBox'
    );
  });
};

const getLinkedTo = (seriesOptions: HighchartsType.SeriesOptionsType): string | undefined => {
  const { linkedTo } = seriesOptions as { linkedTo?: unknown };
  return typeof linkedTo === 'string' ? linkedTo : undefined;
};

const getRuntimeSeriesId = (series: HighchartsType.Series): string | undefined => {
  if (typeof series.options.id === 'string') return series.options.id;
  if (typeof series.userOptions.id === 'string') return series.userOptions.id;
  return undefined;
};

const getSeriesOptionsId = (seriesOptions: StockSeriesOptions): string | undefined => {
  const { id } = seriesOptions as { id?: unknown };
  return typeof id === 'string' ? id : undefined;
};

const getRuntimeLinkedSeriesOptions = (
  chart: HighchartsType.Chart,
  baseSeriesIds: Set<string>
): StockSeriesOptions[] =>
  chart.series.flatMap(series => {
    const linkedTo = getLinkedTo(series.userOptions) ?? getLinkedTo(series.options);
    const seriesId = getRuntimeSeriesId(series);

    if (!linkedTo || !baseSeriesIds.has(linkedTo) || (seriesId && baseSeriesIds.has(seriesId))) {
      return [];
    }

    return [
      {
        ...series.userOptions,
        visible: series.visible
      } as StockSeriesOptions
    ];
  });

export const preserveRuntimeLinkedSeries = (
  options: HighchartsType.Options,
  chart: HighchartsType.Chart
): HighchartsType.Options => {
  const baseSeries = options.series ?? [];
  const baseSeriesIds = new Set(
    baseSeries.map(series => getSeriesOptionsId(series)).filter((id): id is string => Boolean(id))
  );
  const runtimeLinkedSeries = getRuntimeLinkedSeriesOptions(chart, baseSeriesIds);

  if (runtimeLinkedSeries.length === 0) return options;

  return {
    ...options,
    series: [...baseSeries, ...runtimeLinkedSeries]
  };
};

export const createFullScreenLegendSeries = ({
  chartSeries,
  hiddenSeriesIds
}: CreateFullScreenStockOptionsParams): FullScreenLegendSeries[] =>
  chartSeries.map((series, index) => {
    const id = getSeriesId(series, index);
    const context = series.custom;

    return {
      alias: context.Alias,
      code: context.Code,
      color: getSeriesColor(series, index),
      currency: context.Currency,
      id,
      name: getSeriesName(series, index),
      period: context.Period,
      periodType: context.PeriodType,
      timeRef: context.TimeRef,
      units: context.Units,
      visible: !hiddenSeriesIds.includes(id)
    };
  });

export const createFullScreenStockOptions = ({
  chartSeries,
  hiddenSeriesIds,
  tooltip = createFullScreenRolloverTooltip(() => ''),
  useSmallWatermark = false
}: CreateFullScreenStockOptionsParams): HighchartsType.Options => {
  return {
    accessibility: { enabled: false },
    chart: {
      allowMutatingData: false,
      animation: false,
      events: {
        load() {
          applyFullscreenWatermarkLayout(this as FullscreenWatermarkChart, useSmallWatermark);
        },
        redraw() {
          applyFullscreenWatermarkLayout(this as FullscreenWatermarkChart, useSmallWatermark);
        }
      },
      height: null,
      reflow: true,
      style: {
        fontFamily: 'Cairo, sans-serif'
      },
      type: 'line',
      zooming: {
        type: 'x'
      }
    },
    credits: { enabled: false },
    exporting: {
      buttons: {
        contextButton: {
          enabled: false
        }
      },
      enabled: true,
      fallbackToExportServer: false
    },
    legend: { enabled: false },
    lang: {
      rangeSelectorZoom: ''
    },
    navigator: {
      adaptToUpdatedData: false,
      enabled: true
    },
    plotOptions: {
      line: {
        states: {
          hover: {
            lineWidth: 3
          }
        }
      },
      series: {
        animation: false,
        states: {
          hover: {
            enabled: false
          },
          inactive: {
            opacity: 1
          }
        }
      }
    },
    rangeSelector: {
      allButtonsEnabled: true,
      buttonPosition: {
        align: 'left',
        x: 0,
        y: 0
      },
      buttons: RANGE_SELECTOR_BUTTONS,
      buttonSpacing: 8,
      buttonTheme: {
        fill: '#121836',
        height: 22,
        r: 0,
        style: {
          color: '#FFF'
        },
        states: {
          disabled: {
            fill: 'rgba(255, 255, 255, 0.12)',
            style: {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          },
          hover: {
            style: {
              color: '#A1A6AB'
            }
          },
          select: {
            fill: '#E84E0F',
            style: {
              color: '#FFF'
            }
          }
        },
        width: 40
      },
      dropdown: 'never',
      inputBoxHeight: 24,
      inputDateFormat: '%Y-%m-%d',
      inputEditDateFormat: '%Y-%m-%d',
      inputEnabled: false,
      inputStyle: {
        fontSize: '12px'
      },
      labelStyle: {
        display: 'none',
        fontSize: '12px'
      },
      selected: getInitialRangeButtonIndex(chartSeries)
    },
    scrollbar: {
      enabled: true
    },
    series: chartSeries.map((series, index) => toHighchartsSeries(series, index, hiddenSeriesIds)),
    stockTools: {
      gui: {
        buttons: [...STOCK_TOOLS_BUTTONS],
        enabled: true
      }
    },
    title: { text: undefined },
    tooltip,
    xAxis: {
      crosshair: true,
      labels: {
        style: {
          fontSize: AXIS_LABEL_FONT_SIZE
        }
      },
      ordinal: false,
      type: 'datetime'
    },
    yAxis: createFullScreenYAxisOptions()
  };
};

const withoutInitialRangeSelection = (options: HighchartsType.Options): HighchartsType.Options => ({
  ...options,
  rangeSelector: {
    ...options.rangeSelector,
    selected: undefined
  }
});

const useFullScreenChart = ({ chartId, chartSeries }: UseFullScreenChartParams) => {
  const { hasPermission } = usePermission();
  const theme = useTheme();
  const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);
  const createTooltipFormatter = useFullScreenRolloverFormatter();
  const tooltip = useMemo(
    () => createFullScreenRolloverTooltip(createTooltipFormatter, theme.palette.text.primary),
    [createTooltipFormatter, theme.palette.text.primary]
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<HighchartsType.Chart | null>(null);
  const chartIdRef = useRef<string | null>(null);
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<string[]>([]);

  const availableSeriesIds = useMemo(
    () => new Set(chartSeries.map((series, index) => getSeriesId(series, index))),
    [chartSeries]
  );
  const activeHiddenSeriesIds = useMemo(
    () => hiddenSeriesIds.filter(id => availableSeriesIds.has(id)),
    [availableSeriesIds, hiddenSeriesIds]
  );

  const options = useMemo(
    () =>
      createFullScreenStockOptions({
        chartSeries,
        hiddenSeriesIds: activeHiddenSeriesIds,
        tooltip,
        useSmallWatermark
      }),
    [activeHiddenSeriesIds, chartSeries, tooltip, useSmallWatermark]
  );

  const legendSeries = useMemo(
    () =>
      createFullScreenLegendSeries({
        chartSeries,
        hiddenSeriesIds: activeHiddenSeriesIds
      }),
    [activeHiddenSeriesIds, chartSeries]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    if (!chartRef.current || chartIdRef.current !== chartId) {
      chartRef.current?.destroy();
      chartRef.current = Highcharts.stockChart(containerRef.current, options);
      installFullscreenYAxisSync(chartRef.current);
      installFullscreenRangeSelectorSync(chartRef.current);
      applyFullscreenYAxisLayout(chartRef.current, true);
      applyFullscreenRangeSelectorLayout(chartRef.current);
      chartIdRef.current = chartId;
      return;
    }

    const updateOptions = preserveRuntimeLinkedSeries(
      withoutInitialRangeSelection(options),
      chartRef.current
    );
    chartRef.current.update(updateOptions, true, true);
    installFullscreenYAxisSync(chartRef.current);
    installFullscreenRangeSelectorSync(chartRef.current);
    applyFullscreenYAxisLayout(chartRef.current, true);
    applyFullscreenRangeSelectorLayout(chartRef.current);
  }, [chartId, options]);

  useEffect(
    () => () => {
      chartRef.current?.destroy();
      chartRef.current = null;
      chartIdRef.current = null;
    },
    []
  );

  const toggleSeriesVisibility = useCallback((seriesId: string) => {
    setHiddenSeriesIds(prev =>
      prev.includes(seriesId) ? prev.filter(id => id !== seriesId) : [...prev, seriesId]
    );
  }, []);

  return {
    containerRef,
    legendSeries,
    toggleSeriesVisibility
  };
};

export default useFullScreenChart;
